import type { PartnerDePlataforma } from '../../api/ops/backoffice';
import { dinero, type Importe } from '../../logica/dinero';
import { claveMes, nombreMes, textoTipo, type Mes } from '../../partners/logica';

/**
 * Lógica de **Partners** del backoffice (1.23 en `1.20-1.22-backoffice.html?seccion=partners`), sin nada visual: textos
 * del prototipo (`apps`, `partners`, `vapp`, `vlg`, `vpay`) y qué liquidación se ve. El panel no suma ni calcula dinero
 * (G121, G290): cada importe es el `total` que da la API.
 */

const FORMA: Readonly<Record<NonNullable<PartnerDePlataforma['legal_form']>, string>> = { self_employed: 'Autónomo', company: 'Empresa' };

/** Forma jurídica en texto («Empresa», «Autónomo»), o null. */
export const textoForma = (forma: PartnerDePlataforma['legal_form']): string | null => (forma == null ? null : FORMA[forma]);

const unir = (partes: readonly (string | null | undefined)[]): string => partes.filter((x): x is string => x != null && x !== '').join(' · ');

/** «pidió el alta hoy», «… ayer», «… hace 2 días» (`applied_at`, G1145), por días de calendario de quien mira. */
export function cuandoPidio(applied: string | null | undefined, ahora: Date): string | null {
    if (applied === null || applied === undefined || applied === '') {
        return null;
    }
    const d = new Date(applied);
    if (Number.isNaN(d.getTime())) {
        return null;
    }
    const dia = (x: Date) => Date.UTC(x.getFullYear(), x.getMonth(), x.getDate());
    const dias = Math.max(0, Math.round((dia(ahora) - dia(d)) / 86_400_000));
    return dias === 0 ? 'pidió el alta hoy' : dias === 1 ? 'pidió el alta ayer' : `pidió el alta hace ${String(dias)} días`;
}

/** Línea de una solicitud: «Consultor · Empresa · Málaga · pidió el alta hace 2 días» (prototipo `apps`, G1145). */
export function lineaSolicitud(p: PartnerDePlataforma, ahora: Date = new Date()): string {
    return unir([textoTipo(p.type), textoForma(p.legal_form), p.billing_address?.city, cuandoPidio(p.applied_at, ahora)]);
}

/** «3 negocios», «1 negocio» (`business_count`, G1145); sin el dato, null. */
export function textoNegocios(n: number | null | undefined): string | null {
    return n === null || n === undefined ? null : n === 1 ? '1 negocio' : `${String(n)} negocios`;
}

/**
 * Línea de un partner en la liquidación: «Consultor · Empresa · 3 negocios» (prototipo `line`, G1145) y, ya pagada, la
 * referencia de la transferencia (C.66): «… · transferencia TR-2026-10-0087».
 */
export function lineaPartnerLiquidacion(p: PartnerDePlataforma | undefined, referencia: string | null = null): string {
    const ref = referencia === null || referencia.trim() === '' ? null : `transferencia ${referencia.trim()}`;
    return unir([...(p === undefined ? [] : [textoTipo(p.type), textoForma(p.legal_form), textoNegocios(p.business_count)]), ref]);
}

/** «3 por revisar», «1 por revisar» o, sin ninguna, «Ninguna por revisar» (C.70, G1158). */
export const notaSolicitudes = (pendientes: number): string => (pendientes === 0 ? 'Ninguna por revisar' : `${String(pendientes)} por revisar`);

export const SIN_SOLICITUDES = 'No hay solicitudes de partner por revisar. Cuando alguien pida el alta, aparecerá aquí.';

/** «2 por pagar · 95,20 €» o «Todo pagado» (`pending_count` y `pending_total` de la API, G1145: el panel no suma). */
export function notaLiquidacion(pendientes: number, total: Importe): string {
    return pendientes === 0 ? 'Todo pagado' : `${String(pendientes)} por pagar · ${dinero(total)}`;
}

/** «20 % de 285,25 €» bajo el importe (`commission_base`, G1145). */
export const textoBase = (base: Importe): string => `20 % de ${dinero(base)}`;

/** Ventana «Marcar pagada» (prototipo `vpay`, con la referencia de G1146). */
export function textosPago(partner: string, mes: Mes, total: Importe): { titulo: string; texto: string } {
    return {
        titulo: `Pagar a ${partner}`,
        texto: `Marca como pagada la liquidación de ${nombreMes(mes)}: ${dinero(total)}. Hazlo cuando la transferencia haya salido; el partner lo ve en su portal con la referencia.`,
    };
}

/** `payment_reference`: obligatoria, hasta 120 caracteres (G1146). */
export const referenciaValida = (r: string): boolean => r.trim().length >= 1 && r.trim().length <= 120;
export const ERROR_REFERENCIA = 'Escribe la referencia de la transferencia.';

/**
 * El mes cuya liquidación se ve: el último cerrado. La de un mes se cierra el día 5 del siguiente (por la hora de Madrid,
 * G1087): del 5 en adelante, el mes anterior; antes del 5, el de hace dos meses.
 */
export function mesDeLiquidacion(ahora: Date): Mes {
    const partes = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(ahora);
    const v = (tipo: string) => Number(partes.find((p) => p.type === tipo)?.value ?? '0');
    const atras = v('day') >= 5 ? 1 : 2;
    const total = v('year') * 12 + (v('month') - 1) - atras;
    return { y: Math.floor(total / 12), m: total % 12 };
}

/** «Liquidación de septiembre». */
export const tituloLiquidacion = (mes: Mes): string => `Liquidación de ${nombreMes(mes)}`;

/** `period` de `listPlatformPartnerSettlements` (`AAAA-MM`, G1103). */
export const periodoDe = claveMes;

/** Textos de la ventana de aprobar o rechazar (prototipo `vapp`). */
export function textosDecision(nombre: string, decision: 'approve' | 'reject'): { titulo: string; texto: string; accion: string } {
    return decision === 'reject'
        ? { titulo: `Rechazar a ${nombre}`, texto: 'Le avisamos por email de que no seguimos adelante. Si escribes el motivo, va en ese email y queda en Auditoría.', accion: 'Rechazar' }
        : { titulo: `Aprobar a ${nombre}`, texto: 'Le avisamos por email y entra en su portal de partners. Desde ese momento su código empieza a atribuirle negocios.', accion: 'Aprobar' };
}

/** Lo que queda en la fila tras decidir (prototipo `chip`). */
export const textoDecidida = (decision: 'approve' | 'reject'): string => (decision === 'approve' ? 'Aprobada' : 'Rechazada');

export const TEXTO_DATOS_LEGALES = 'Salen en sus facturas de comisiones y en su contrato. El partner los ve en su portal y no puede cambiarlos.';

export const FORMAS: readonly { valor: 'self_employed' | 'company'; texto: string }[] = [
    { valor: 'self_employed', texto: 'Autónomo' },
    { valor: 'company', texto: 'Empresa' },
];

export interface FormDatosLegales {
    forma: 'self_employed' | 'company';
    nombre: string;
    nif: string;
}

/** El formulario empieza con lo que hay. El NIF completo no sale nunca (G1096): se ve el enmascarado. */
export const formDatosLegalesDe = (p: PartnerDePlataforma): FormDatosLegales => ({ forma: p.legal_form ?? 'company', nombre: p.legal_name, nif: p.tax_id_masked ?? '' });

/** Solo lo que cambia (el NIF, si se ha escrito uno nuevo), con el motivo (G237). */
export function cambiosDatosLegales(p: PartnerDePlataforma, f: FormDatosLegales, motivo: string): { legal_form?: 'self_employed' | 'company'; legal_name?: string; tax_id?: string; reason: string } {
    const nif = f.nif.trim().toUpperCase();
    return {
        ...(f.forma !== (p.legal_form ?? 'company') ? { legal_form: f.forma } : {}),
        ...(f.nombre.trim() !== p.legal_name ? { legal_name: f.nombre.trim() } : {}),
        ...(nif !== '' && nif !== (p.tax_id_masked ?? '').toUpperCase() ? { tax_id: nif } : {}),
        reason: motivo.trim(),
    };
}
